<template>
  <input
    type="text"
    :value="maskedValue"
    @input="handleInput"
    @blur="isFocused = false"
    @focus="isFocused = true"
    ref="numberInput"
  />
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from 'vue';

// Define Props
const props = defineProps({
  // Locale for formatting (e.g., 'en-US', 'de-DE')
  locale: {
    type: String,
    default: 'en-US'
  },
});

// Define Emits
const modelValue = defineModel({
  modelValue: {
    type: [Number, String, null],
    default: null
  }
});

// Reactive State
const maskedValue = ref('');
const internalUnmaskedValue = ref(modelValue);
const isFocused = ref(false);

// Template Ref
const numberInput = ref(null); // Reference to the input element

// Functions
const cleanNumber = (value) => {
  const cleaned = String(value ?? '').replace(/\D/g, '')

  return cleaned === ''
    ? null
    : cleaned;
};

const formatNumber = (numberString) => {
  const number = parseInt(numberString, 10);
  if (isNaN(number)) {
    return '';
  }

  try {
    // Use Intl.NumberFormat for locale-aware formatting
    const formatter = new Intl.NumberFormat(props.locale, {
      useGrouping: true,
      maximumFractionDigits: 0
    });
    return formatter.format(number);
  } catch (e) {
    console.error('Error formatting number:', e);
    return String(number);
  }
};

const handleInput = (event) => {
  const input = event.target;
  const caretPos = input.selectionStart;
  const rawValue = input.value;
  const cleanedValue = cleanNumber(rawValue);

  // Store the previous masked value to help calculate caret position
  const previousMaskedValue = maskedValue.value;
  internalUnmaskedValue.value = cleanedValue;

  // Emit the cleaned value (as a number or string)
  modelValue.value = cleanedValue !== null
    ? parseInt(cleanedValue, 10)
    : null;
  const newMaskedValue = formatNumber(cleanedValue);
  // maskedValue.value = null;
  maskedValue.value = newMaskedValue;

  // Count separators before caret in the *previous* masked value
  const separatorsBeforeCaret = (previousMaskedValue.substring(0, caretPos).match(/\D/g) || []).length;
  const cleanedCaretPos = caretPos - separatorsBeforeCaret;
  const newSeparatorsBeforeCaret = (newMaskedValue.substring(0, cleanedCaretPos + separatorsBeforeCaret).match(/\D/g) || []).length;

  let newCaretPos = cleanedCaretPos + newSeparatorsBeforeCaret;
  newCaretPos = Math.min(newCaretPos, newMaskedValue.length);

  // Set the caret position in the next tick to ensure the DOM is updated
  nextTick(() => {
    if (numberInput.value) {
      numberInput.value.selectionStart = numberInput.value.selectionEnd = newCaretPos;
    }
  });
};

watch(() => modelValue, (newValue) => {
  // Check if the new value is different from the internal state.
  if (!isFocused.value && newValue !== internalUnmaskedValue.value) {
    internalUnmaskedValue.value = newValue;
    maskedValue.value = formatNumber(cleanNumber(newValue));
  }
});

// Watch the internal unmasked value to update the masked value when needed
watch(internalUnmaskedValue, (newValue) => {
  if (!isFocused.value) {
    maskedValue.value = formatNumber(cleanNumber(newValue));
  }
});

onMounted(() => {
  maskedValue.value = formatNumber(cleanNumber(modelValue.value));
});

</script>
